import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Image from 'remix-image';
import { MuiRemixLink } from '~/components/shared/links';
import { baseImgUrl } from '~/utils/constants';
import Button3D from '../shared/Button3D';

type ImgProps = {
  src: string;
  srcSet?: string;
  props?: any;
};

type BulkCategory = {
  text: string;
  to: string;
  img: ImgProps;
};

function Category({ title, img, to }: { title: string; img: ImgProps; to: string }) {
  return (
    <MuiRemixLink href={to} underline="none" aria-label={to}>
      <Box
        bgcolor="secondary.main"
        height="100%"
        width="100%"
        display="flex"
        justifyContent="flex-end"
        sx={{
          borderRadius: '24px',
          p: 2,
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            cursor: 'pointer',
          },
        }}
      >
        <Typography
          variant="h5"
          textAlign="left"
          position="absolute"
          left="16px"
          bottom="16px"
          zIndex={100}
          color="text.primary"
        >
          {title}
        </Typography>
        <Image
          src={img.src}
          srcSet={img.srcSet}
          alt={title}
          width={400}
          height={400}
          style={{
            ...img.props,
            width: '100%',
            height: 'auto',
          }}
        />
      </Box>
    </MuiRemixLink>
  );
}

export default function BulkSwag() {
  const bulkCategories: BulkCategory[] = [
    {
      text: 'Drinkware',
      to: '/shop/bulk-swag/drinkware',
      img: {
        src: `${baseImgUrl}clock-cup.png`,
        srcSet: `${baseImgUrl}clock-cup.png, ${baseImgUrl}clock-cup-2x.png 2x, ${baseImgUrl}clock-cup-3x.png 3x`,
        props: {
          maxWidth: '400px',
          maxHeight: '350px',
          transform: 'scale(1.2)',
        },
      },
    },
    {
      text: 'Bags',
      to: '/shop/bulk-swag/totes-duffles',
      img: {
        src: `${baseImgUrl}bag-purple.png`,
        srcSet: `${baseImgUrl}bag-purple.png, ${baseImgUrl}bag-purple-2x.png 2x, ${baseImgUrl}bag-purple-3x.png 3x`,
        props: {
          maxWidth: '400px',
          maxHeight: '350px',
          transform: 'scale(1.2) translateX(-10px)',
        },
      },
    },
    {
      text: 'Office',
      to: '/shop/bulk-swag/notebooks-pens',
      img: {
        src: `${baseImgUrl}employee-desk.png`,
        srcSet: `${baseImgUrl}employee-desk.png, ${baseImgUrl}employee-desk-2x.png 2x, ${baseImgUrl}employee-desk-3x.png 3x`,

        props: {
          maxWidth: '400px',
          maxHeight: '276px',
          transform: 'scale(1.5) translateX(30px)',
        },
      },
    },
    {
      text: 'Tech',
      to: '/shop/bulk-swag/tech-items',
      img: {
        src: `${baseImgUrl}employee-chair.png`,
        srcSet: `${baseImgUrl}employee-chair.png, ${baseImgUrl}employee-chair-2x.png 2x, ${baseImgUrl}employee-chair-3x.png 3x`,

        props: {
          maxWidth: '400px',
          maxHeight: '276px',
          transform: 'scale(1.1) translateX(30px)',
        },
      },
    },
    {
      text: 'Apparel',
      to: '/shop/bulk-swag/apparel',
      img: {
        src: `${baseImgUrl}employee-walking.png`,
        srcSet: `${baseImgUrl}employee-walking.png, ${baseImgUrl}employee-walking-2x.png 2x, ${baseImgUrl}employee-walking-3x.png 3x`,

        props: {
          maxWidth: '400px',
          maxHeight: '276px',
          transform: 'scale(1.6) translateX(20px)',
        },
      },
    },
  ];

  return (
    <Box sx={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F9F6EF 100%)' }}>
      <Container sx={{ pt: 10, pb: { xs: 6, sm: 10 } }}>
        <Stack alignItems="center" mb={{ xs: 8, sm: 12 }}>
          <Typography variant="h4" mb={3} width={{ xs: '100%', sm: '80%', md: '60%', lg: '25%' }} textAlign="center">
            Bulk Swag Fit For All Your Needs
          </Typography>

          <Typography
            variant="subtitle1"
            mb={3}
            width={{ xs: '100%', sm: '80%', md: '65%' }}
            mx="auto"
            textAlign="center"
          >
            The joy of swag and getting new company merch should be shared with everyone. That’s why we make the process
            easy and affordable for bulk orders. Check out our bulk pricing below or contact us for more information.
          </Typography>

          <Button3D href="/shop/bulk-swag" maxWidth={250}>
            Book Your Bulk Order
          </Button3D>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Category title={bulkCategories[0].text} img={bulkCategories[0].img} to={bulkCategories[0].to} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Category title={bulkCategories[1].text} img={bulkCategories[1].img} to={bulkCategories[1].to} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Category title={bulkCategories[2].text} img={bulkCategories[2].img} to={bulkCategories[2].to} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Category title={bulkCategories[3].text} img={bulkCategories[3].img} to={bulkCategories[3].to} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Category title={bulkCategories[4].text} img={bulkCategories[4].img} to={bulkCategories[4].to} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
